// Owned by task 2.2 of the dashboard-redesign spec (Requirement 6).
//
// Reads the current-period service-charge collection rate (owned by the
// `service-charge-reconciliation` spec, which itself reconciles to the GL
// per Constitution Rule 2) and exposes it as `WidgetResult<CollectionRate>`
// for `Widget_CollectionRate` to consume. This hook is READ-ONLY: no
// mutation, no re-aggregation that bypasses the GL.
//
// Fetch contract (see design.md §Low-Level Design §Fetch contract):
//   - Starts in `{ status: "loading" }`.
//   - On mount and every `scope` change: an `AbortController`-guarded GET.
//   - Empty state when `demandedPence === 0` (no demands raised this period).
//   - Ready state carries a parsed `CollectionRate` with `rate ∈ [0, 1]`.
//     Any rate outside that interval is treated as an error with a fixed
//     "Invalid collection rate returned by server" message — never rendered
//     as a real number.
//   - Error state carries an `errorId` (from the `X-Error-Id` response header
//     if present, else a locally-generated UUID v4) so the scoped error card
//     can render it verbatim for support (Requirement 14).
//   - `retry` re-invokes the fetch by bumping a retry counter.
//   - On unmount: aborts the in-flight fetch.
//
// The `api` client (frontend/src/services/api.ts) already attaches the Clerk
// JWT and the `x-clerk-subdomain` tenant header via interceptors — this hook
// MUST NOT re-implement any of that.
//
// Coordination note: task 2.1 (`useDashboardArrears`) is being written in
// parallel and shares the same fetch pattern. The small helpers below are
// duplicated inline on purpose; a later refactor task will consolidate them
// into a `useWidgetFetch` primitive after both hooks have landed.

import { useCallback, useEffect, useState } from "react";
import type { AxiosError } from "axios";
import api from "../../../services/api";
import type { BuildingScope, WidgetResult, CollectionRate } from "../types";

/** User-safe message shown in the scoped error card. Never leaks server text. */
const USER_SAFE_ERROR_MESSAGE = "Could not load collection rate";

/** Fixed copy for the rate-out-of-bounds validation failure (per task 2.2). */
const INVALID_RATE_MESSAGE = "Invalid collection rate returned by server";

/**
 * Generate a UUID v4. Prefers `globalThis.crypto.randomUUID` when available;
 * falls back to a small `Math.random`-based hex formatter for environments
 * that don't expose the WebCrypto API (older test runners, JSDOM shims, etc.).
 * Never used to authenticate anything — only to correlate a UI error card to
 * a log entry when the server didn't return an `X-Error-Id` header.
 */
function generateLocalErrorId(): string {
  const cryptoRef = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
    return cryptoRef.randomUUID();
  }
  // RFC 4122 v4 shape: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx where y ∈ {8,9,a,b}.
  const rand = (): number => Math.floor(Math.random() * 16);
  const hex = (n: number): string => n.toString(16);
  let out = "";
  for (let i = 0; i < 36; i += 1) {
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      out += "-";
    } else if (i === 14) {
      out += "4";
    } else if (i === 19) {
      out += hex((rand() & 0x3) | 0x8);
    } else {
      out += hex(rand());
    }
  }
  return out;
}

/**
 * Extract an `X-Error-Id` header from an axios error's response. Case-
 * insensitive per RFC 7230; different transports normalise headers
 * differently (Node http lowercases everything, JSDOM leaves them as-is).
 */
function extractErrorIdFromAxios(err: unknown): string | undefined {
  if (!err || typeof err !== "object") return undefined;
  const response = (err as AxiosError).response;
  if (!response || !response.headers) return undefined;
  const headers = response.headers as
    | Record<string, string | undefined>
    | { get?: (key: string) => string | null };
  // Some axios versions expose headers as an AxiosHeaders instance with a
  // `get(key)` method; others expose a plain record. Handle both defensively.
  if (typeof (headers as { get?: unknown }).get === "function") {
    const raw = (headers as { get: (key: string) => string | null }).get(
      "x-error-id",
    );
    if (typeof raw === "string" && raw.length > 0) return raw;
  }
  const record = headers as Record<string, string | undefined>;
  const candidate =
    record["x-error-id"] ??
    record["X-Error-Id"] ??
    record["X-ERROR-ID"];
  return typeof candidate === "string" && candidate.length > 0
    ? candidate
    : undefined;
}

/**
 * Validate that an unknown response body has the exact shape of
 * `CollectionRate`. Returns the typed value on success or `undefined` on any
 * structural mismatch — the caller then transitions to the error state per
 * the fetch contract, so we never trust an unexpected payload.
 *
 * Note: the `rate` bounds check (must lie in `[0, 1]`) is intentionally NOT
 * done here; that's a semantic invariant handled by the effect below so it
 * can surface a distinct user-facing message.
 */
function parseCollectionRate(body: unknown): CollectionRate | undefined {
  if (!body || typeof body !== "object") return undefined;
  const b = body as Record<string, unknown>;
  const { periodLabel, demandedPence, collectedPence, rate, perBuilding } = b;

  if (typeof periodLabel !== "string" || periodLabel.length === 0) {
    return undefined;
  }
  if (
    typeof demandedPence !== "number" ||
    !Number.isFinite(demandedPence) ||
    !Number.isInteger(demandedPence)
  ) {
    return undefined;
  }
  if (
    typeof collectedPence !== "number" ||
    !Number.isFinite(collectedPence) ||
    !Number.isInteger(collectedPence)
  ) {
    return undefined;
  }
  if (typeof rate !== "number" || !Number.isFinite(rate)) {
    return undefined;
  }
  if (!Array.isArray(perBuilding)) return undefined;

  const cleanedPerBuilding: CollectionRate["perBuilding"] = [];
  for (const row of perBuilding) {
    if (!row || typeof row !== "object") return undefined;
    const r = row as Record<string, unknown>;
    if (
      typeof r.buildingId !== "string" ||
      typeof r.buildingName !== "string" ||
      typeof r.rate !== "number" ||
      !Number.isFinite(r.rate)
    ) {
      return undefined;
    }
    cleanedPerBuilding.push({
      buildingId: r.buildingId,
      buildingName: r.buildingName,
      rate: r.rate,
    });
  }

  return {
    periodLabel,
    demandedPence,
    collectedPence,
    rate,
    perBuilding: cleanedPerBuilding,
  };
}

export function useCollectionRate(
  scope: BuildingScope,
): WidgetResult<CollectionRate> {
  const [retryCounter, setRetryCounter] = useState(0);
  const [result, setResult] = useState<WidgetResult<CollectionRate>>({
    status: "loading",
  });

  const retry = useCallback(() => {
    setRetryCounter((n) => n + 1);
  }, []);

  const scopeKind = scope.kind;
  const scopeBuildingId = scope.kind === "single" ? scope.buildingId : null;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    // Reset to loading on scope/retry change so consumers see the skeleton
    // between fetches instead of stale ready data.
    setResult({ status: "loading" });

    const buildingId =
      scopeKind === "all" ? "ALL" : (scopeBuildingId ?? "ALL");
    const url =
      `/service-charge-reconciliation/collection-rate` +
      `?period=current&buildingId=${encodeURIComponent(buildingId)}`;

    api
      .get(url, { signal: controller.signal })
      .then((response) => {
        if (cancelled) return;
        const parsed = parseCollectionRate(response?.data);
        if (!parsed) {
          setResult({
            status: "error",
            errorId: generateLocalErrorId(),
            message: "Unexpected response shape",
            retry,
          });
          return;
        }

        if (parsed.demandedPence === 0) {
          setResult({ status: "empty" });
          return;
        }

        if (parsed.rate < 0 || parsed.rate > 1) {
          setResult({
            status: "error",
            errorId: generateLocalErrorId(),
            message: INVALID_RATE_MESSAGE,
            retry,
          });
          return;
        }

        setResult({ status: "ready", data: parsed });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // The browser/axios raises `CanceledError` (or a `DOMException`
        // named `AbortError`) when the AbortController fires. That path is
        // the caller's intent, not a failure — swallow silently.
        if (controller.signal.aborted) return;
        const errorId =
          extractErrorIdFromAxios(err) ?? generateLocalErrorId();
        setResult({
          status: "error",
          errorId,
          message: USER_SAFE_ERROR_MESSAGE,
          retry,
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // `scope.kind` and `scope.buildingId` are the only inputs; `retryCounter`
    // triggers a re-fetch on `retry()`. `retry` and `api` are stable refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKind, scopeBuildingId, retryCounter]);

  return result;
}
